/**
 * TenantHeader: branded top navigation driven by the tenant config.
 *
 * Applies the tenant's brand colors as CSS variables (--tenant-primary /
 * --tenant-accent) on the header element so the whole subtree can use
 * Tailwind's `tenant-primary` / `tenant-accent` colors.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectBranding, useTenantStore } from '../stores/tenant';
import { displayNameFor, selectIsAuthenticated, useAuthStore } from '../stores/auth';
import { LanguageSwitcher } from './LanguageSwitcher';

const FALLBACK_NAME = 'CivicLines';

function navLinkClass(isActive: boolean): string {
  return [
    'whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
    isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white',
  ].join(' ');
}

/** Brand mark: a 2×2 block of district colors — the product in one glyph. */
function BrandMark({ colors }: { colors: readonly string[] | undefined }): JSX.Element {
  const c = [colors?.[0] ?? '#2F6DB5', colors?.[1] ?? '#E8912D', colors?.[2] ?? '#1E9E78', colors?.[3] ?? '#D1543A'];
  return (
    <span aria-hidden="true" className="grid h-9 w-9 grid-cols-2 gap-[2px] overflow-hidden rounded-lg bg-white/90 p-[3px] shadow-sm">
      {c.map((color, i) => (
        <span key={i} className="rounded-[3px]" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

function AccountMenu(): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center gap-2">
        <Link
          to="/signin"
          className="hidden rounded-lg px-3 py-2 text-sm font-semibold text-white/85 hover:bg-white/10 hover:text-white sm:block"
        >
          {t('nav.signIn')}
        </Link>
        <Link
          to="/signup"
          className="whitespace-nowrap rounded-lg bg-white px-3 py-2 text-sm font-semibold text-tenant-primary shadow-sm hover:bg-white/90 sm:px-3.5"
        >
          <span className="sm:hidden">{t('nav.signUpShort')}</span>
          <span className="hidden sm:inline">{t('nav.signUp')}</span>
        </Link>
      </div>
    );
  }

  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/');
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className="hidden max-w-40 truncate rounded-lg px-2 py-2 text-sm font-medium text-white/90 sm:block"
        title={user.email}
      >
        {displayNameFor(user)}
      </span>
      <button
        type="button"
        onClick={() => void handleLogout()}
        className="rounded-lg px-3 py-2 text-sm font-medium text-white/85 hover:bg-white/10 hover:text-white"
      >
        {t('nav.signOut')}
      </button>
    </div>
  );
}

/**
 * ParticipateMenu: a dropdown grouping the public participation tools.
 *
 * "Draw a plan" and "Communities of interest" are both ways to take part,
 * and more tools will join them — one menu keeps the navbar tight instead
 * of growing a new top-level link per tool. The button highlights whenever
 * any child route is active.
 */
function ParticipateMenu(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const active = location.pathname.startsWith('/map') || location.pathname.startsWith('/coi');

  // Close on navigation, Escape, or an outside click.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    function onPointerDown(event: PointerEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open ]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('nav.participate')}
        className={navLinkClass(active)}
      >
        {/* Narrow phones: a menu icon, so the city name, language and
            sign-in still fit; from 400px up, the word. */}
        <svg
          aria-hidden="true"
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          className="min-[400px]:hidden"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
        <span className="hidden min-[400px]:inline">
          {t('nav.participate')} <span aria-hidden="true" className="ml-0.5 text-xs">{open ? '▴' : '▾'}</span>
        </span>
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t('nav.participateMenuLabel')}
          className="absolute left-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-stone-200 bg-white py-1 text-stone-800 shadow-lg"
        >
          <NavLink
            to="/map"
            role="menuitem"
            className="block px-4 py-2.5 hover:bg-stone-100"
          >
            <span className="block text-sm font-medium text-ink">{t('nav.drawPlan')}</span>
            <span className="block text-xs text-stone-500">
              {t('nav.drawPlanDesc')}
            </span>
          </NavLink>
          <NavLink
            to="/coi"
            role="menuitem"
            className="block px-4 py-2.5 hover:bg-stone-100"
          >
            <span className="block text-sm font-medium text-ink">
              {t('nav.coi')}
            </span>
            <span className="block text-xs text-stone-500">
              {t('nav.coiDesc')}
            </span>
          </NavLink>
        </div>
      )}
    </div>
  );
}

export function TenantHeader(): JSX.Element {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const signedIn = useAuthStore(selectIsAuthenticated);
  // Staff tools only for staff accounts; staff sign-in is in the footer.
  const isStaff = signedIn && (role === 'staff' || role === 'admin');
  const tenant = useTenantStore((s) => s.tenant);
  const { primary, accent, logoUrl } = selectBranding(tenant);
  const name = tenant?.name ?? FALLBACK_NAME;
  // Map workspaces are full-bleed; align the header with them there.
  const fullBleed = ['/map', '/coi'].includes(useLocation().pathname);

  return (
    <header
      className="sticky top-0 z-40 bg-tenant-primary text-white shadow-[0_1px_0_rgba(0,0,0,0.08)]"
      // Scoped brand variables: children (and Tailwind tenant-* colors) inherit.
      style={
        {
          '--tenant-primary': primary,
          '--tenant-accent': accent,
          '--tenant-on-primary': '#ffffff',
          backgroundColor: 'var(--tenant-primary)',
        } as React.CSSProperties
      }
    >
      <div
        className={`mx-auto flex h-16 items-center justify-between px-4 sm:px-6 ${
          fullBleed ? 'max-w-none' : 'max-w-7xl lg:px-8'
        }`}
      >
        <div className="flex items-center gap-2">
          <Link to="/" className="flex min-w-0 items-center gap-2.5" aria-label={t('nav.homeAria', { name })}>
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-9 w-9 rounded-lg bg-white/90 object-contain p-1" />
            ) : (
              <BrandMark colors={tenant?.districts.colors} />
            )}
            <span className="leading-tight">
              <span className="block max-w-[9rem] truncate text-base font-bold tracking-tight sm:max-w-none">{name}</span>
              {tenant && (
                <span className="hidden text-[11px] font-semibold tracking-[0.04em] text-white/60 sm:block">
                  {t('nav.onCivicLines')}
                </span>
              )}
            </span>
          </Link>

          <nav aria-label="Primary" className="ml-2 flex items-center gap-1 sm:ml-6">
            <div className="md:hidden">
              <ParticipateMenu />
            </div>
            <NavLink to="/map" className={({ isActive }) => `hidden md:block ${navLinkClass(isActive)}`}>
              {t('nav.drawPlan')}
            </NavLink>
            <NavLink to="/coi" className={({ isActive }) => `hidden md:block ${navLinkClass(isActive)}`}>
              {t('nav.communitiesShort')}
            </NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-1">
          <LanguageSwitcher />
          {isStaff && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `hidden rounded-lg px-2.5 py-2 text-xs font-semibold lg:block ${isActive ? 'text-white' : 'text-white/55 hover:text-white'}`
              }
            >
              {t('nav.staff')}
            </NavLink>
          )}
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}
